import { create } from 'zustand';

export interface ExecutionTrace {
  workflowId: string;
  activeNodeId: string | null;
  completedNodeIds: string[];
  failedNodeId: string | null;
  failedMessage: string | null;
  running: boolean;
}

interface ExecutionTraceState {
  traces: Record<string, ExecutionTrace>;
  startTrace: (workflowId: string) => void;
  markNodeStart: (workflowId: string, nodeId: string) => void;
  markNodeComplete: (workflowId: string, nodeId: string) => void;
  markNodeFailed: (workflowId: string, nodeId: string, message: string) => void;
  finishTrace: (workflowId: string) => void;
  clearTrace: (workflowId: string) => void;
}

const emptyTrace = (workflowId: string): ExecutionTrace => ({
  workflowId,
  activeNodeId: null,
  completedNodeIds: [],
  failedNodeId: null,
  failedMessage: null,
  running: false,
});

export const useExecutionTraceStore = create<ExecutionTraceState>((set) => ({
  traces: {},

  startTrace: (workflowId) =>
    set((state) => ({
      traces: {
        ...state.traces,
        [workflowId]: { ...emptyTrace(workflowId), running: true },
      },
    })),

  markNodeStart: (workflowId, nodeId) =>
    set((state) => {
      const current = state.traces[workflowId] ?? emptyTrace(workflowId);
      return {
        traces: {
          ...state.traces,
          [workflowId]: { ...current, activeNodeId: nodeId, running: true },
        },
      };
    }),

  markNodeComplete: (workflowId, nodeId) =>
    set((state) => {
      const current = state.traces[workflowId] ?? emptyTrace(workflowId);
      const completedNodeIds = current.completedNodeIds.includes(nodeId)
        ? current.completedNodeIds
        : [...current.completedNodeIds, nodeId];
      return {
        traces: {
          ...state.traces,
          [workflowId]: {
            ...current,
            activeNodeId: null,
            completedNodeIds,
            failedNodeId: null,
            failedMessage: null,
            running: true,
          },
        },
      };
    }),

  markNodeFailed: (workflowId, nodeId, message) =>
    set((state) => {
      const current = state.traces[workflowId] ?? emptyTrace(workflowId);
      return {
        traces: {
          ...state.traces,
          [workflowId]: {
            ...current,
            activeNodeId: nodeId,
            failedNodeId: nodeId,
            failedMessage: message,
            running: false,
          },
        },
      };
    }),

  finishTrace: (workflowId) =>
    set((state) => {
      const current = state.traces[workflowId] ?? emptyTrace(workflowId);
      return {
        traces: {
          ...state.traces,
          [workflowId]: {
            ...current,
            activeNodeId: null,
            running: false,
          },
        },
      };
    }),

  clearTrace: (workflowId) =>
    set((state) => {
      const next = { ...state.traces };
      delete next[workflowId];
      return { traces: next };
    }),
}));
